import {
  map,
  mergeMap,
  flatMap,
  catchError,
  withLatestFrom,
} from 'rxjs/operators'
import { of, from, Observable, merge } from 'rxjs'
import * as actions from './actions'
import * as LDClient from 'launchdarkly-js-client-sdk'
import { ofType } from 'redux-observable'

let client

export const init = (action$) => {
  return action$.pipe(
    ofType(actions.LAUNCH_DARKLY_INIT),
    mergeMap((action) => {
      if (
        action &&
        action.payload &&
        action.payload.id &&
        action.payload.user &&
        action.payload.user.key
      ) {
        const { id, user } = action.payload
        client = LDClient.initialize(id, user)
      } else {
        return of({
          type: actions.LAUNCH_DARKLY_INIT_REJECTED,
          payload: new Error(
            'Valid id and user object required: { id: YOUR_ID_HERE, user: { key: USER_EMAIL_HERE } }',
          ),
        })
      }
      return from(client.waitForInitialization()).pipe(
        flatMap(() => {
          return merge(
            of({
              type: actions.LAUNCH_DARKLY_INIT_FULFILLED,
              payload: action.payload.user, //nullchecked above
            }),
            of({
              type: actions.LAUNCH_DARKLY_READY,
            }),
          )
        }),
        catchError((payload) => {
          return of({
            type: actions.LAUNCH_DARKLY_INIT_REJECTED,
            payload,
          })
        }),
      )
    }),
  )
}

export const ready = (action$, state$) => {
  return action$.pipe(
    ofType(actions.LAUNCH_DARKLY_READY),
    withLatestFrom(state$),
    mergeMap(() => {
      return from(client.waitUntilReady()).pipe(
        map(() => {
          return {
            type: actions.LAUNCH_DARKLY_READY_FULFILLED,
          }
        }),
      )
    }),
    catchError((payload) => {
      return of({
        type: actions.LAUNCH_DARKLY_READY_REJECTED,
        payload,
      })
    }),
  )
}

export const getFeatureFlag = (action$, state$) => {
  return action$.pipe(
    ofType(actions.LAUNCH_DARKLY_GET_FEATURE_FLAG),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      if (state.darklyFeatureFlags.isReady && client) {
        if (action && action.payload && action.payload.featureFlag) {
          const { featureFlag } = action.payload
          return of(client.variation(featureFlag)).pipe(
            flatMap((payload) => {
              if (payload !== undefined) {
                return of({
                  type: actions.LAUNCH_DARKLY_UPDATE_FEATURE_FLAGS,
                  payload: { [featureFlag]: payload },
                })
              } else {
                return of({
                  type: actions.LAUNCH_DARKLY_GET_FEATURE_FLAG_REJECTED,
                  payload: new Error(
                    `Feature flag "${featureFlag}" doesn't exist`,
                  ),
                })
              }
            }),
          )
        }
        return of({
          type: actions.LAUNCH_DARKLY_GET_FEATURE_FLAG_REJECTED,
          payload: new Error(
            'featureFlag object required: { featureFlag: YOUR_FEATURE_FLAG_HERE }',
          ),
        })
      }
      return of({
        type: actions.LAUNCH_DARKLY_GET_FEATURE_FLAG_REJECTED,
        payload: new Error('LaunchDarkly not ready'),
      })
    }),
    catchError((payload) => {
      return of({
        type: actions.LAUNCH_DARKLY_GET_FEATURE_FLAG_REJECTED,
        payload,
      })
    }),
  )
}

export const getAllFeatureFlags = (action$, state$) => {
  return action$.pipe(
    ofType(actions.LAUNCH_DARKLY_GET_ALL_FEATURE_FLAGS),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      if (state.darklyFeatureFlags.isReady && client) {
        return of(client.allFlags()).pipe(
          flatMap((payload) => {
            return of({
              type: actions.LAUNCH_DARKLY_UPDATE_FEATURE_FLAGS,
              payload,
            })
          }),
        )
      }
      return of({
        type: actions.LAUNCH_DARKLY_GET_ALL_FEATURE_FLAGS_REJECTED,
        payload: new Error('LaunchDarkly not ready'),
      })
    }),
    catchError((payload) => {
      return of({
        type: actions.LAUNCH_DARKLY_GET_ALL_FEATURE_FLAGS_REJECTED,
        payload,
      })
    }),
  )
}

export const subscribeSpecificFeatureFlag = (action$, state$) => {
  return action$.pipe(
    ofType(actions.LAUNCH_DARKLY_SUBSCRIBE_SPECIFIC_FEATURE_FLAG),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      if (state.darklyFeatureFlags.isReady && client) {
        if (action && action.payload && action.payload.featureFlag) {
          const key = action && action.payload && action.payload.featureFlag
          return merge(
            Observable.create((observer) => {
              client.on(`change:${key}`, (value) => {
                return observer.next({
                  type: actions.LAUNCH_DARKLY_UPDATE_FEATURE_FLAGS,
                  payload: { [key]: value },
                })
              })
            }),
            of({
              type:
                actions.LAUNCH_DARKLY_SUBSCRIBE_SPECIFIC_FEATURE_FLAG_FULFILLED,
              payload: action.payload,
            }),
          )
        } else {
          return of({
            type:
              actions.LAUNCH_DARKLY_SUBSCRIBE_SPECIFIC_FEATURE_FLAG_REJECTED,
            payload: new Error(
              'featureFlag object required: { featureFlag: YOUR_FEATURE_FLAG_HERE }',
            ),
          })
        }
      }
      return of({
        type: actions.LAUNCH_DARKLY_SUBSCRIBE_SPECIFIC_FEATURE_FLAG_REJECTED,
        payload: new Error('LaunchDarkly not ready'),
      })
    }),
  )
}

export const subscribeAllFeatureFlags = (action$, state$) => {
  return action$.pipe(
    ofType(actions.LAUNCH_DARKLY_SUBSCRIBE_ALL_FEATURE_FLAGS),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      if (state.darklyFeatureFlags.isReady && client) {
        return merge(
          Observable.create((observer) => {
            client.on('change', (payload) => {
              const flags = Object.entries(payload).reduce(
                (accumulator, flag) => {
                  const key = flag[0]
                  const value = flag[1].current
                  accumulator[key] = value
                  return accumulator
                },
                {},
              )
              return observer.next({
                type: actions.LAUNCH_DARKLY_UPDATE_FEATURE_FLAGS,
                payload: flags,
              })
            })
          }),
          of({
            type: actions.LAUNCH_DARKLY_SUBSCRIBE_ALL_FEATURE_FLAGS_FULFILLED,
          }),
        )
      }
      return of({
        type: actions.LAUNCH_DARKLY_SUBSCRIBE_ALL_FEATURE_FLAGS_REJECTED,
        payload: new Error('LaunchDarkly not ready'),
      })
    }),
  )
}

export const identifyUser = (action$, state$) => {
  return action$.pipe(
    ofType(actions.LAUNCH_DARKLY_IDENTIFY_USER),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      if (state.darklyFeatureFlags.isInitialized && client) {
        if (
          action &&
          action.payload &&
          action.payload.user &&
          action.payload.user.key
        ) {
          const newUser = action.payload.user
          let identifyComplete
          let identifyError
          const promise = new Promise((resolve, reject) => {
            identifyComplete = resolve
            identifyError = reject
          })
          client.identify(newUser, null, function(error, flags) {
            if (flags) {
              identifyComplete(flags)
            } else {
              identifyError(error)
            }
          })
          return from(promise).pipe(
            map(function(userFlags) {
              return {
                type: actions.LAUNCH_DARKLY_IDENTIFY_USER_FULFILLED,
                payload: {
                  user: newUser,
                  flags: userFlags,
                },
              }
            }),
            catchError(function(error) {
              return of({
                type: actions.LAUNCH_DARKLY_IDENTIFY_USER_REJECTED,
                payload: error,
              })
            }),
          )
        }
        return of({
          type: actions.LAUNCH_DARKLY_IDENTIFY_USER_REJECTED,
          payload: new Error(
            'user object required: { user: { key: YOUR_USER_KEY HERE } }',
          ),
        })
      }
      return of({
        type: actions.LAUNCH_DARKLY_IDENTIFY_USER_REJECTED,
        payload: new Error('LaunchDarkly not initialized'),
      })
    }),
    catchError((payload) => {
      return of({
        type: actions.LAUNCH_DARKLY_IDENTIFY_USER_REJECTED,
        payload,
      })
    }),
  )
}
